import React, { useState } from 'react';
import { withTranslation } from 'react-i18next';
import { isMobileOnly } from 'react-device-detect';
import {
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
} from '@mui/material';

import { usePolicyState } from '@core/myta/components/PolicyModal';
import { useMytaverse } from '@providers/MytaverseProvider';
import { useMytaverseEvent } from '@core/dashboard/providers';
import { useClientSpecificProfileInformation } from '@hooks/clientSpecificUtils';
import {
  Header,
  ModalContainer,
} from '@core/dashboard/components/PopoverModal';
import { ProfileScreensEnum } from '../interfaces';
import AvatarBox from '@components/UserProfileModalNew/AvatarBox';

import { ReactComponent as EmailIcon } from '@public/images/email.svg';
import { ReactComponent as PrivacyIcon } from '@public/images/privacy.svg';
import { ReactComponent as LogoutIcon } from '@public/images/account_exit.svg';
import { ReactComponent as NextIcon } from '@public/images/next.svg';
import { ReactComponent as SettingsIcon } from '@public/images/settings.svg';
import { ReactComponent as ThreeDotsVerticalIcon } from '@public/images/threeDotsVertical.svg';
import { ReactComponent as PersonIcon } from '@public/images/person.svg';

import { IMainMenuList } from './interfaces';
import { useStyles } from './styles';
import { AnalyticsType } from '@interfaces/analytics';
import More from '../More/More';

const MainScreen = ({
  onClose,
  setActiveScreen,
  onOpenLogoutModal,
  t: translate,
}: IMainMenuList) => {
  const classes = useStyles();
  const [moreOpen, setMoreOpen] = useState(false);
  const { user, trackAnalytics } = useMytaverse();
  const isOnDashboard = !!useMytaverseEvent();
  const { profileUserName, profileEmail } =
    useClientSpecificProfileInformation();

  const { setOpen: setOpenPrivacy } = usePolicyState();

  const getHeader = () => {
    if (isMobileOnly && moreOpen) {
      return (
        <Header
          onClose={onClose}
          onBack={() => setMoreOpen(false)}
          text={translate('userProfile.more')}
        />
      );
    } else {
      return <Header onClose={onClose} />;
    }
  };

  return (
    <>
      <ModalContainer header={getHeader()} isScrollable={false}>
        {moreOpen && <More onClose={onClose} />}
        {!moreOpen && (
          <Box sx={classes.root}>
            <AvatarBox
              avatar={user.profileImage?.url ?? ''}
              avatarFallback={user.name || profileEmail}
              name={profileUserName}
            />
            <MenuList sx={classes.menu}>
              <MenuItem sx={classes.link}>
                <ListItemIcon>
                  <EmailIcon />
                </ListItemIcon>
                <ListItemText sx={classes.linkText}>
                  {profileEmail}
                </ListItemText>
              </MenuItem>

              {!isMobileOnly && (
                <>
                  <MenuItem
                    sx={classes.link}
                    onClick={() => {
                      setOpenPrivacy(true);
                      trackAnalytics(AnalyticsType.ViewPrivacyPolicy, {
                        ref: 'menu',
                      });
                      onClose();
                    }}
                  >
                    <ListItemIcon>
                      <PrivacyIcon />
                    </ListItemIcon>
                    <ListItemText sx={classes.linkText}>
                      {translate('userProfile.privacy')}
                    </ListItemText>
                    <NextIcon />
                  </MenuItem>

                  {isOnDashboard && (
                    <MenuItem
                      sx={classes.link}
                      onClick={() =>
                        setActiveScreen(ProfileScreensEnum.SETTINGS)
                      }
                    >
                      <ListItemIcon>
                        <SettingsIcon />
                      </ListItemIcon>
                      <ListItemText sx={classes.linkText}>
                        {translate('userProfile.settings')}
                      </ListItemText>
                      <NextIcon />
                    </MenuItem>
                  )}
                </>
              )}
              {isMobileOnly && (
                <>
                  <Divider />
                  <Box sx={classes.menuOptionMobile}>
                    {isOnDashboard && (
                      <MenuItem
                        sx={classes.link}
                        onClick={() =>
                          setActiveScreen(ProfileScreensEnum.SETTINGS)
                        }
                      >
                        <ListItemIcon>
                          <SettingsIcon />
                        </ListItemIcon>
                        <ListItemText sx={classes.linkText}>
                          {translate('userProfile.settings')}
                        </ListItemText>
                      </MenuItem>
                    )}
                    <MenuItem sx={classes.link} disabled>
                      <ListItemIcon>
                        <PersonIcon />
                      </ListItemIcon>
                      <ListItemText sx={classes.linkText}>
                        {translate('userProfile.myAvatars')}
                      </ListItemText>
                    </MenuItem>
                    <MenuItem
                      sx={classes.link}
                      onClick={() => {
                        setMoreOpen(true);
                      }}
                    >
                      <ListItemIcon>
                        <ThreeDotsVerticalIcon />
                      </ListItemIcon>
                      <ListItemText sx={classes.linkText}>
                        {translate('userProfile.more')}
                      </ListItemText>
                    </MenuItem>
                  </Box>
                </>
              )}

              <Divider />

              <MenuItem
                sx={classes.link}
                onClick={() => {
                  onOpenLogoutModal();
                }}
              >
                <ListItemIcon>
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText sx={classes.linkText}>
                  {translate('userProfile.logOut')}
                </ListItemText>
              </MenuItem>
            </MenuList>
          </Box>
        )}
      </ModalContainer>
    </>
  );
};

export default withTranslation()(MainScreen);
